import React from 'react';
import { createPortal } from 'react-dom';
import { UserWithRole, Referent, ReferentFunction } from '../../../../../shared/services/userService';

interface ReferentsModalProps {
  user: UserWithRole;
  referents: Referent[];
  referentFunctions: ReferentFunction[];
  newReferentFunction: string;
  newReferentDescription: string;
  updating: boolean;
  onFunctionChange: (functionName: string) => void;
  onDescriptionChange: (description: string) => void;
  onAssign: () => void;
  onRemove: (functionName: string) => void;
  onClose: () => void;
}

const ReferentsModal: React.FC<ReferentsModalProps> = ({
  user,
  referents,
  referentFunctions,
  newReferentFunction,
  newReferentDescription,
  updating,
  onFunctionChange,
  onDescriptionChange,
  onAssign,
  onRemove,
  onClose
}) => {
  const getSelectedReferent = (functionId: string) => referents.find(r => r.function === functionId);

  const getReferentActiveState = (functionId: string) => {
    const selected = referents.find(r => r.function === functionId);
    if (!selected) return 'none';
    return selected.is_active ? 'active' : 'inactive';
  };

  const extractReferentType = (referentFunction: string): string => {
    const u = referentFunction.toUpperCase();
    if (u.includes('ADMIN')) return 'Admin';
    if (u.includes('CM')) return 'CM';
    if (u.includes('MODO')) return 'Modo';
    if (u.includes('SCÉNARISTE') || u.includes('SCENARISTE')) return 'Scénariste';
    if (u.includes('GR') || u.includes('GROUPE') || u.includes('RÉFLEXION') || u.includes('REFLEXION')) return 'GR';
    return 'Inconnu';
  };

  const getReferentTypeColor = (referentType: string) => {
    const u = referentType.toUpperCase();
    if (u.includes('CM')) return 'bg-red-500/20 text-red-300 border-red-500/40';
    if (u.includes('ADMIN')) return 'bg-orange-500/20 text-orange-300 border-orange-500/40';
    if (u.includes('MODO')) return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    if (u.includes('SCÉNARISTE') || u.includes('SCENARISTE')) return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
    if (u.includes('GR') || u.includes('GROUPE') || u.includes('RÉFLEXION') || u.includes('REFLEXION')) return 'bg-purple-500/20 text-purple-300 border-purple-500/40';
    return 'bg-slate-500/20 text-slate-300 border-slate-500/40';
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="rounded-xl border border-[#243052]/90 bg-[#0a1128] p-6 shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-2">
          Gérer les référents — {user.username}
        </h3>
        <p className="text-xs text-slate-400 mb-5">
          Assignez le statut de référent à cet utilisateur. Vous pouvez assigner plusieurs fonctions à la même personne.
        </p>

        {referentFunctions.length > 0 && (
          <div className="mb-5 rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4">
            <h4 className="text-sm font-semibold text-slate-200 mb-3">Assigner ou mettre à jour</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Fonction de référent</label>
                <select
                  value={newReferentFunction}
                  onChange={(e) => onFunctionChange(e.target.value)}
                  className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
                >
                  <option value="">Sélectionner une fonction</option>
                  {referentFunctions.map((func) => {
                    const state = getReferentActiveState(func.id);
                    const suffix = state === 'active' ? ' (actif)' : state === 'inactive' ? ' (inactif)' : '';
                    return (
                      <option key={func.id} value={func.id}>
                        {func.name} - {func.description}{suffix}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Description (optionnel)</label>
                <textarea
                  value={newReferentDescription}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                  placeholder="Description des responsabilités..."
                  rows={2}
                  className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60 resize-none"
                />
              </div>
              <button
                onClick={onAssign}
                disabled={updating || !newReferentFunction}
                className="w-full rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 py-2.5 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 disabled:opacity-40 transition-colors"
              >
                {updating
                  ? 'Mise à jour...'
                  : getSelectedReferent(newReferentFunction)
                    ? 'Mettre à jour le référent'
                    : 'Assigner le statut de référent'}
              </button>
            </div>
          </div>
        )}

        {referents.length === 0 ? (
          <p className="text-slate-400 text-center py-8 text-sm">Aucun référent assigné</p>
        ) : (
          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Référents assignés</h4>
            {referents.map((referent) => (
              <div
                key={referent.id}
                className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 flex items-center justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-white font-semibold text-sm">{referent.function}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                      referent.is_active
                        ? getReferentTypeColor(referent.function)
                        : 'bg-slate-500/20 text-slate-400 border-slate-500/40 opacity-50'
                    }`}>
                      {extractReferentType(referent.function)}
                    </span>
                    {!referent.is_active && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-300 border border-red-500/40">
                        Inactif
                      </span>
                    )}
                  </div>
                  {referent.description && (
                    <p className="text-slate-400 text-xs mb-1">{referent.description}</p>
                  )}
                  <p className="text-slate-500 text-xs">
                    Assigné le {new Date(referent.assigned_at).toLocaleDateString('fr-FR')}
                    {referent.assigned_by_username && ` par ${referent.assigned_by_username}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => { onFunctionChange(referent.function); onDescriptionChange(referent.description || ''); }}
                    disabled={updating}
                    className="rounded-lg border border-white/15 bg-black/30 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-black/45 disabled:opacity-50 transition-colors"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => onRemove(referent.function)}
                    disabled={updating}
                    className="rounded-lg border border-red-500/35 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                  >
                    Retirer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-5 w-full rounded-lg border border-white/15 bg-black/30 py-2.5 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>,
    document.body
  );
};

export default ReferentsModal;
